import { useEffect, useState } from 'react';
import PageTitle from '../components/PageTitle';
import useFleetAircraftCount from '../hooks/useFleetAircraftCount';
import SafeImage from '../components/SafeImage';

const sections = [
  {
    title: 'FENIX SIMULATIONS A320 / A321',
    platform: 'MSFS 2020 / 2024',
    cards: [
      { title: 'Fleet Variety', text: 'Complete fleet diversity featuring the A320ceo with both wing-tip-fence and sharklet, alongside the A321ceo. All of our liveries have accurate panel mismatch and custom roughness maps for almost every registration.', images: ['fnxflt5.png', 'fnxflt4.png', 'fnxflt8.png', 'fnxflt6.png', 'fnxflt1.png', 'fnxflt2.png', 'fnxflt7.png', 'fnxflt3.png'] },
      { title: 'Detailed Textures', text: 'Ultra-high definition 8K textures that capture every rivet, dirt streak, and fluid leak. We utilize custom roughness maps to replicate the exact look of a hardworking low-cost airliner.', images: ['fnxtxt2.png', 'fnxtxt3.png', 'fnxtxt4.png', 'fnxtxt6.png', 'fnxtxt5.png', 'fnxtxt7.png'] },
      { title: 'Custom Cabin', text: 'Step inside a fully customized Wizz Air interior. Custom safety cards and galley carts—the experience continues behind the cockpit door.', images: ['fnxcab1.png', 'fnxcab2.png', 'fnxcab3.png', 'fnxcab4.png', 'fnxcab5.png', 'fnxcab6.png'] },
    ],
  },
  {
    title: 'FLIGHT SIM LABS A321neo',
    platform: 'MSFS 2020 / 2024',
    cards: [
      { title: 'A321neo Exclusive', text: 'High-fidelity A321neo recreation specifically for MSFS with custom decals and updated roughness maps.', images: ['fslfront.png', 'fslwmr.png', 'fsllgi.png', 'fsllrd.png', 'fslwnm.png', 'fslwmr1.png'] },
      { title: 'Attention to Detail', text: 'Custom engine textures to mirror GTF engines until they are released by FSLabs, plus up-to-date mismatching panels.', images: ['fslmid1.png', 'fslmid2.png', 'fslmid3.png', 'fslmid4.png', 'fslmid5.png'] },
      { title: 'Visual Fidelity', text: 'Precise color matching and accurate decal positioning for all standard and special liveries in the fleet.', images: ['fsldet1.png', 'fsluna.png', 'fsldet2.png', 'fsldet3.png', 'fsldet4.png', 'fsldet5.png'] },
    ],
  },
  {
    title: 'TOLISS A320 / A321',
    platform: 'X-PLANE 11 / 12',
    cards: [
      { title: 'Full Fleet', text: 'Extensive fleets for both ToLiss NEO and CEO aircraft, CARDA mod compatibility, and frequent livery updates to match their respective real-world state.', images: ['tlwnd.png', 'tlwdr.png', 'tlwmr1.png', 'tlwmr2.png', 'tlwmr3.png', 'tlwdx.png', 'tllzu1.png', 'tllzu.png', 'tllxi.png'] },
      { title: 'Detailed Textures', text: 'High-quality 4K resolution textures with good optimization, detail, and X-Plane 12/11 compatibility.', images: ['tlmid1.png', 'tlmid2.png', 'tlmid3.png', 'tlmid4.png', 'tlmid5.png'] },
      { title: 'Custom Cabin', text: 'A custom-painted cabin with high quality, accuracy, and detail on all A320 family aircraft.', images: ['tlcab1.png', 'tlcab2.png', 'tlcab3.png'] },
    ],
  },
];

const imagePath = (name) => `/images/livery/${name}`;

function FleetCard({ card, onOpen }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (card.images.length < 2) return undefined;
    const interval = window.setInterval(() => setActive((current) => (current + 1) % card.images.length), 4000);
    return () => window.clearInterval(interval);
  }, [card.images.length]);

  return (
    <article className="feature-card fleet-card">
      <button className="slider-container" onClick={() => onOpen(card.images, active)} aria-label={`Open ${card.title} gallery`}>
        {card.images.map((image, index) => (
          <SafeImage key={image} src={imagePath(image)} alt="" className={index === active ? 'active' : ''} />
        ))}
      </button>
      <div className="fleet-card-copy">
        <h3 className="feature-title">{card.title}</h3>
        <p className="feature-text fleet-card-text">{card.text}</p>
      </div>
    </article>
  );
}

export default function FleetPage() {
  const fleetCount = useFleetAircraftCount();

  const [modal, setModal] = useState(null);

  useEffect(() => {
    document.body.style.overflow = modal ? 'hidden' : '';
    const onKeyDown = (event) => {
      if (!modal) return;
      if (event.key === 'Escape') setModal(null);
      if (event.key === 'ArrowLeft') setModal((current) => ({ ...current, index: (current.index - 1 + current.images.length) % current.images.length }));
      if (event.key === 'ArrowRight') setModal((current) => ({ ...current, index: (current.index + 1) % current.images.length }));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [modal]);

  const changeSlide = (direction) => {
    setModal((current) => ({ ...current, index: (current.index + direction + current.images.length) % current.images.length }));
  };

  return (
    <>
      <PageTitle>vWZZ - Fleet</PageTitle>
      <div className="page-container">
        <h1 className="page-title">THE vWZZ HANGAR</h1>
        <p className="page-subtitle">{fleetCount} Real World Airframes. Ultra-High Definition. Up to date.</p>

        <div className="staff-grid fleet-intro-grid">
          <article className="staff-card">
            <h3>About Our Liveries</h3>
            <div className="staff-details fleet-about-copy">
              <p>Every livery in our hangar is based on a real-world airframe. We focus on correct decals, mismatching panels, and details that match how the aircraft actually looked in service. No shortcuts—just consistent, research-driven work that fits naturally into normal line operations and long-term fleet realism.</p>
              <p>Our fleet is created by experienced, professional painters, including <strong>Benek</strong>, a well-known and respected livery author, alongside a small team of contributors with extensive simulator and real-world reference experience.</p>
              <div className="painter-list">
                <div className="painter-group"><h4>Fenix (MSFS)</h4><p>Benek</p></div>
                <div className="painter-group"><h4>FSLabs (MSFS)</h4><p>Benek, Dejw, Rob</p></div>
                <div className="painter-group"><h4>ToLiss (XP)</h4><p>Mati, Harry, Gabor</p></div>
              </div>
            </div>
          </article>
        </div>

        {sections.map((section, sectionIndex) => (
          <section className={`fleet-section ${sectionIndex ? 'fleet-section-spaced' : ''}`} key={section.title}>
            <h2 className="section-title fleet-section-title">{section.title}</h2>
            <p className="page-subtitle fleet-platform">{section.platform}</p>
            <div className="features-grid">
              {section.cards.map((card) => <FleetCard card={card} onOpen={(images, index) => setModal({ images, index })} key={card.title} />)}
            </div>
          </section>
        ))}
      </div>

      {modal && (
        <div id="image-modal" className="open" role="dialog" aria-modal="true" aria-label="Livery gallery" onClick={() => setModal(null)}>
          <button className="modal-arrow prev-arrow" onClick={(event) => { event.stopPropagation(); changeSlide(-1); }} aria-label="Previous image">‹</button>
          <SafeImage id="modal-img" src={imagePath(modal.images[modal.index])} alt="Selected aircraft livery" onClick={(event) => event.stopPropagation()} />
          <button className="modal-arrow next-arrow" onClick={(event) => { event.stopPropagation(); changeSlide(1); }} aria-label="Next image">›</button>
          <div className="close-hint">Click background or press Escape to close</div>
        </div>
      )}
    </>
  );
}
